import { useState, useEffect, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal';
import { UploadedTileGrid } from '@/components/upload/UploadedTileGrid';
import { photosApi } from '@/lib/api';

// ─── Admin per-album photo manager ───────────────────────────────────────────
// Opens from a clickable album row on AdminOrderDetailPage's "Photos client"
// card. Shows THAT album's customer-uploaded photos in the same fast,
// virtualized, iPhone-Photos-style square grid the customer review screen uses
// (<UploadedTileGrid>), with select / "Tout sélectionner" / single-✕ delete /
// "Supprimer la sélection (N)".
//
// REUSE, not rebuild:
//   - Grid: <UploadedTileGrid> verbatim (square, react-window virtualized).
//   - Select/delete UX shape mirrors MagicUploadPage's <UploadedGallery>
//     (selected:Set, toggle, allSelected/toggleSelectAll, runDelete + confirm).
//
// What's DIFFERENT from the customer gallery: the data layer. The customer side
// talks token APIs (uploadLinkApi.listPhotos / deletePhotos). Admin is already
// authenticated, so we use the session/photo-id APIs:
//   - LIST   photosApi.list(sessionId)  → scoped to this album's session
//   - DELETE photosApi.delete(photoId)  → single; we loop in bounded-parallel
//
// Per-album scoping is intrinsic: the list is scoped by sessionId and each
// delete is keyed by the photo's own id, so Album 1 deletes can never touch
// Album 2's photos.

type AdminPhoto = {
  id: string;
  tray_signed_url?: string | null;
  signed_url: string | null;
};

// Delete in bounded-parallel chunks so a large multi-select doesn't fire 40
// requests at once. Promise.allSettled → we keep the ids that actually deleted.
async function deleteInChunks(
  ids: string[],
  chunkSize = 8,
): Promise<{ deleted: string[]; failed: number }> {
  const deleted: string[] = [];
  let failed = 0;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const results = await Promise.allSettled(chunk.map((id) => photosApi.delete(id)));
    results.forEach((r, idx) => {
      if (r.status === 'fulfilled') deleted.push(chunk[idx]);
      else failed += 1;
    });
  }
  return { deleted, failed };
}

export function AdminAlbumPhotosModal({
  isOpen,
  onClose,
  sessionId,
  albumLabel,
  onChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string | null;
  albumLabel: string;
  onChanged: () => void;
}) {
  const [photos, setPhotos] = useState<AdminPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch this album's photos on open / session change. Reset all state so a
  // previously-open album never leaks its selection or list into the next one.
  useEffect(() => {
    if (!isOpen || !sessionId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSelected(new Set());
    setDeleteError(null);
    setPhotos([]);
    (async () => {
      try {
        const data = await photosApi.list(sessionId);
        if (cancelled) return;
        setPhotos(
          data.photos.map((p) => ({
            id: p.id,
            tray_signed_url: p.tray_signed_url,
            signed_url: p.signed_url,
          })),
        );
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, sessionId]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const allSelected = photos.length > 0 && selected.size === photos.length;
  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === photos.length ? new Set() : new Set(photos.map((p) => p.id)),
    );
  }, [photos]);

  const runDelete = useCallback(async (ids: string[]) => {
    if (ids.length === 0 || deleting) return;
    const ok = window.confirm(
      `Supprimer ${ids.length} photo${ids.length > 1 ? 's' : ''} ? Cette action est définitive.`,
    );
    if (!ok) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const { deleted, failed } = await deleteInChunks(ids);
      if (deleted.length > 0) {
        const removed = new Set(deleted);
        setPhotos((prev) => prev.filter((p) => !removed.has(p.id)));
        setSelected((prev) => {
          const next = new Set(prev);
          deleted.forEach((id) => next.delete(id));
          return next;
        });
        // Tell the parent so the order's photo summary (grid + counts) refreshes.
        onChanged();
      }
      if (failed > 0) {
        setDeleteError(
          `${failed} photo${failed > 1 ? 's' : ''} n'${failed > 1 ? 'ont' : 'a'} pas pu être supprimée${failed > 1 ? 's' : ''}.`,
        );
      }
    } catch (err) {
      setDeleteError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  }, [deleting, onChanged]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Photos — ${albumLabel}`} size="xl">
      <div className="p-5">
        {/* Header row: count + select-all toggle (mirrors the customer gallery) */}
        {!loading && !loadError && photos.length > 0 && (
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-700">
              {photos.length} photo{photos.length > 1 ? 's' : ''}
            </div>
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-semibold text-brand-600 hover:underline"
            >
              {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span
              className="w-7 h-7 rounded-full border-[3px] border-gray-200 border-t-brand-500 animate-spin"
            />
          </div>
        ) : loadError ? (
          <div className="rounded-xl px-4 py-3 text-sm bg-red-50 border border-red-200 text-red-700">
            Impossible de charger les photos de cet album.
          </div>
        ) : (
          <UploadedTileGrid
            photos={photos.map((p) => ({
              id: p.id,
              thumbUrl: p.tray_signed_url ?? p.signed_url ?? null,
              selected: selected.has(p.id),
            }))}
            selectMode
            onToggleSelect={toggle}
            onDelete={deleting ? undefined : (id) => { void runDelete([id]); }}
            emptyHint="Aucune photo."
          />
        )}

        {deleteError && (
          <div className="mt-3 text-xs text-red-700">
            {deleteError}
          </div>
        )}

        {/* Delete-selection action bar */}
        {selected.size > 0 && (
          <button
            type="button"
            disabled={deleting}
            onClick={() => void runDelete([...selected])}
            className="mt-4 w-full px-4 py-3 rounded-xl text-sm font-semibold text-white transition active:scale-[0.99] disabled:opacity-60"
            style={{ background: '#DC2626', minHeight: 44 }}
          >
            {deleting ? 'Suppression…' : `Supprimer la sélection (${selected.size})`}
          </button>
        )}
      </div>
    </Modal>
  );
}

export default AdminAlbumPhotosModal;
